import { compact, isArray, isString } from 'lodash';
import { FieldName } from 'react-hook-form';

import { ChronicDiseaseSimple } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { assertUnreachable } from '@dreryk/react-common';
import { TextEditorValue } from '@dreryk/react-text-editor';

import { PreInterviewItem } from '../../common/preInterview/PreInterviewPreviewItem.types';

import { DiagnosisTableBean } from './tabs/diagnoses/DiagnosesFormTab.types';
import { ProcedureTableBean } from './tabs/procedures/procedures.types';
import { VisitFormBean } from './visitForm.types';
import { DiagnosesChangeData, ProceduresChangeData, VisitDetailType } from './VisitFormSynchronization.event';

const getPreInterviewContentAsTextEditorValue = (preInterviewItems: PreInterviewItem[]): TextEditorValue => {
   return TextEditorValue.fromFormatted(
      preInterviewItems
         .map((item) => {
            const question = `${item.questionNumber}. ${item.questionContent}`;
            const answer = item.answerContent ?? '';

            return `<p>${question}</p><p>${answer || '<br>'}</p>`;
         })
         .join('')
   );
};

const getTextEditorValue = (value: string | PreInterviewItem[] | TextEditorValue): TextEditorValue => {
   if (isArray(value)) {
      return getPreInterviewContentAsTextEditorValue(value);
   }

   if (isString(value)) {
      return TextEditorValue.fromFormatted(value);
   }

   return value;
};

export const createTextDetailChangeHandler = () => (
   currentValue: TextEditorValue | null,
   nextValue: TextEditorValue | PreInterviewItem[] | string | null | undefined
): TextEditorValue | null => {
   if (!nextValue) {
      return currentValue;
   }

   if (!currentValue) {
      return getTextEditorValue(nextValue);
   }

   return TextEditorValue.fromFormatted(
      `${currentValue.getFormattedValue()}<p>${getTextEditorValue(nextValue).getFormattedValue()}</p>`
   );
};

export const getFieldName = (visitDetailType: VisitDetailType): FieldName<VisitFormBean> => {
   switch (visitDetailType) {
      case 'diagnoses':
         return 'diagnoses.search';
      case 'subjectiveAssessment':
         return 'subjectiveAssessments';
      default:
         return visitDetailType;
   }
};

type DiagnosesChangeHandlerOptions = {
   chronicDiseases: ChronicDiseaseSimple[];
   createVisitDiagnosis: (diagnosis: DiagnosisTableBean) => Promise<void>;
   updateDiagnosisAdditionalInformation: (diagnosis: DiagnosisTableBean) => Promise<void>;
};

export const createDiagnosesChangeHandler = (options: DiagnosesChangeHandlerOptions) => async (
   currentDiagnoses: DiagnosisTableBean[],
   diagnosesChangeData: DiagnosesChangeData
): Promise<DiagnosisTableBean[]> => {
   const { value, behavior } = diagnosesChangeData;
   const mainDiagnosisExists = currentDiagnoses.some((diagnosis) => diagnosis.main);
   const setFirstDiagnosisAsMain = Boolean(currentDiagnoses.length === 0 && diagnosesChangeData.value.length === 1);
   const currentDiagnosesMap = new Map(currentDiagnoses.map((diagnosis) => [diagnosis.code, diagnosis]));
   const chronicDiseaseCodes: string[] = compact(
      options.chronicDiseases.map((chronicDisease) => chronicDisease.diagnosis?.getCode().asString())
   );
   const newAdditionalInformationMap = value.reduce<Map<string, string>>((map, diagnosis) => {
      const currentDiagnosis = currentDiagnosesMap.get(diagnosis.code);

      if (diagnosis.additionalInformation && !currentDiagnosis?.additionalInformation) {
         map.set(diagnosis.code, diagnosis.additionalInformation);
      }

      return map;
   }, new Map());

   const nextDiagnoses = value.reduce<DiagnosisTableBean[]>((diagnoses, diagnosis) => {
      const diseaseNotExists = !currentDiagnosesMap.has(diagnosis.code);
      if (diseaseNotExists) {
         const chronic = chronicDiseaseCodes.includes(diagnosis.code);
         const main = mainDiagnosisExists ? false : diagnosis.main ?? setFirstDiagnosisAsMain;

         diagnoses.push({
            name: diagnosis.name,
            code: diagnosis.code,
            additionalInformation: diagnosis.additionalInformation ?? null,
            chronic: chronic,
            main: main,
         });
      }

      return diagnoses;
   }, []);

   const updatedCurrentDiagnoses = currentDiagnoses.map(
      (diagnosis): DiagnosisTableBean => {
         return {
            ...diagnosis,
            additionalInformation:
               diagnosis.additionalInformation ?? newAdditionalInformationMap.get(diagnosis.code) ?? null,
         };
      }
   );

   const updatedCurrentDiagnosesWithNewAdditionalInformation = updatedCurrentDiagnoses.filter((diagnosis) =>
      newAdditionalInformationMap.has(diagnosis.code)
   );

   await Promise.all([
      ...nextDiagnoses.map(options.createVisitDiagnosis),
      ...updatedCurrentDiagnosesWithNewAdditionalInformation.map(options.updateDiagnosisAdditionalInformation),
   ]);

   if (behavior === 'prepend') {
      return [...nextDiagnoses, ...updatedCurrentDiagnoses];
   }

   return [...updatedCurrentDiagnoses, ...nextDiagnoses];
};

export const createProceduresChangeHandler = () => (
   proceduresChangeData: ProceduresChangeData
): ProcedureTableBean[] => {
   const { value, behavior } = proceduresChangeData;

   switch (behavior) {
      case 'replace':
         return value;
      default:
         return assertUnreachable(behavior);
   }
};
